/**
 * FigmaLandingPageA — faithful port of legacy's homepage arm A (Figma
 * "Landing Page_V3.1"), `Waitlist/variants/figmaA/FigmaLandingPageA.jsx`.
 *
 * Monolithic by design: one JSX file, one CSS file and local assets, plus the
 * motion wrappers and `landingChrome.css` nav styles beside it. This is a
 * port, not a redesign.
 */
import { useState } from "react";
import { useTranslations } from "next-intl";
import { AnimatePresence, motion, useReducedMotion } from "../../motion";
import BrandLockup from "../../BrandLockup";
import WaitlistFooter from "../../components/WaitlistFooter";
import { FadeUp, FadeIn, FadeLeft, FadeRight, ScaleIn } from "../../motionUtils";
import "./figmaVariantA.css";
import "../../styles/landingChrome.css";
import { assetSrc } from "../../assetSrc";
import { billingAmount } from "../../billingDisplay";
import { HERO_LOG_IN_CTA, HERO_NEW_HERE_CTA } from "../../ctaVariants";
import {
  SIGNED_IN_BEFORE_ATTRIBUTE,
  SIGNED_IN_BEFORE_SCRIPT,
} from "../../../../lib/auth/signedInBefore";

import heroImage from "./assets/landing-a-hero-compressed.jpg";
import timecapsuleImage from "./assets/landing-a-timecapsule.png";
import testimonialImage from "./assets/testimonial.jpg";
const introVideo = "/static/reflect/media/Homepage_intro_with_captions.mp4";
import sortIcon from "./assets/sortIcon.svg";
import scatterPlotIcon from "./assets/scatterPlotIcon.svg";
import chronicIcon from "./assets/chronicIcon.svg";
import dynamicFeedIcon from "./assets/dynamicFeedIcon.svg";
import networkNodeIcon from "./assets/networkNodeIcon.svg";
import psychologyIcon from "./assets/psychologyIcon.svg";
import checkCircleIcon from "./assets/checkCircleIcon.svg";
import { accent } from "../accent";

const STAGGER = 0.08;
const stagger = (index, base = 0) => base + index * STAGGER;

/* ── Data ──────────────────────────────────────────────────────────── */
//
// Copy lives in the catalog under `web.home`; these lists only fix
// the order, the catalog key and anything that is not copy (icons, ids).

const painCards = [
  { id: "writing", icon: sortIcon },
  { id: "scattered", icon: scatterPlotIcon },
  { id: "forgetting", icon: chronicIcon },
];

const featureCards = ["natural", "private", "growth", "ai"];

const steps = [
  { id: "record", num: "1" },
  { id: "reflect", num: "2" },
  { id: "insight", num: "3" },
  { id: "change", num: "4" },
];

const deepDiveCards = [
  { id: "context", icon: dynamicFeedIcon },
  { id: "patterns", icon: networkNodeIcon },
  { id: "understanding", icon: psychologyIcon },
];

/** Keyed by `BILLING_PRICES` key, which is also the plan's catalog key. */
const pricingPlans = [
  { id: "pricing_monthly", priceKey: "monthly" },
  { id: "pricing_yearly", priceKey: "annual" },
  { id: "pricing_lifetime", priceKey: "lifetime" },
];

const checkItems = ["perspective", "shifts", "timeline"];

const faqs = ["audience", "video", "privacy", "insights", "integration", "frequency", "cost"];

/* ── Nav chrome ────────────────────────────────────────────────────── */

function NavPrimaryButton({ children, onClick, testId }) {
  return (
    <button className="figma-c__btn-primary" type="button" data-test-id={testId} onClick={onClick}>
      <div className="figma-c__btn-inner">
        <span className="figma-c__btn-label">{children}</span>
      </div>
    </button>
  );
}

/* ── Sub-components ────────────────────────────────────────────────── */

const FAQItem = ({ answer, id, index, question }) => {
  const [open, setOpen] = useState(false);
  const reduced = useReducedMotion();

  return (
    <FadeUp delay={stagger(index, 0.02)} className="figma-a__faq-item">
      <button
        className="figma-a__faq-trigger"
        type="button"
        data-test-id={`landing-faq-toggle-${id}`}
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
      >
        <span className="figma-a__faq-question">{question}</span>
        <span className={`figma-a__faq-icon ${open ? "figma-a__faq-icon--open" : ""}`}>
          <svg viewBox="0 0 11.3075 6.7075" fill="none">
            <path
              d="M5.65375 6.7075L0 1.05375L1.05375 0L5.65375 4.6L10.2537 0L11.3075 1.05375L5.65375 6.7075Z"
              fill="#002428"
            />
          </svg>
        </span>
      </button>
      <AnimatePresence initial={false}>
        {open ? (
          <motion.div
            className="figma-a__faq-answer"
            initial={reduced ? false : { opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={reduced ? { opacity: 0 } : { opacity: 0, height: 0 }}
            transition={reduced ? { duration: 0 } : { duration: 0.28 }}
          >
            <p>{answer}</p>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </FadeUp>
  );
};

const StepperDot = () => (
  <svg className="figma-a__step-dot" viewBox="0 0 24 24" fill="none">
    <path
      d="M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z"
      fill="#C6EBEE"
    />
    <circle cx="12" cy="12" r="4" fill="#00818C" />
  </svg>
);

/* ── Main Component ────────────────────────────────────────────────── */

/**
 * Arm A. `afterPainPoints` and `afterVision` are slots arm B fills with its
 * strips (#1070): rendered right after the Pain points section and right after
 * the dark Time capsule section. Both default to `null`, which renders nothing,
 * so arm A's own output is unchanged.
 */
const FigmaLandingPageA = ({
  isAuthenticated,
  offersLogIn,
  onCTAClick,
  afterPainPoints = null,
  afterVision = null,
}) => {
  const t = useTranslations("web.home");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const reduced = useReducedMotion();
  const primaryCtaLabel = isAuthenticated ? t("cta.signedIn") : t("cta.startJournal");

  const handlePrimaryClick = (location) => {
    setMobileMenuOpen(false);
    onCTAClick(location);
  };

  const handleNewHereClick = (event) => {
    // A modified click opens the form in another tab, as any link does.
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    event.preventDefault();
    handlePrimaryClick(HERO_NEW_HERE_CTA);
  };

  const closeMenu = () => setMobileMenuOpen(false);

  return (
    <div
      className="figma-a"
      {...{ [SIGNED_IN_BEFORE_ATTRIBUTE]: offersLogIn ? "" : undefined }}
      // The script below sets the attribute before hydration, which renders
      // without it; React then sets it itself on the next render.
      suppressHydrationWarning
    >
      {/* Before anything else, so the hero is painted with the right label. */}
      <script dangerouslySetInnerHTML={{ __html: SIGNED_IN_BEFORE_SCRIPT }} />
      {/* ── 1. NAV ─────────────────────────────────────────────────── */}
      <div className="figma-c">
        <nav className="figma-c__nav" role="navigation" aria-label={t("nav.ariaLabel")}>
          <div className="figma-c__nav-inner">
            <a
              href="#top"
              aria-label={t("nav.homeLink")}
              className="figma-c__nav-logo-link"
              data-test-id="landing-nav-home"
            >
              <BrandLockup />
            </a>
            <div className="figma-c__nav-right">
              <div className="figma-c__nav-links">
                <a href="#pricing" className="figma-c__nav-link" data-test-id="landing-nav-pricing">
                  {t("nav.pricing")}
                </a>
                <a href="#faq" className="figma-c__nav-link" data-test-id="landing-nav-faq">
                  {t("nav.faq")}
                </a>
                <a href="/sign-in" className="figma-c__nav-link" data-test-id="landing-nav-log-in">
                  {t("nav.logIn")}
                </a>
                <NavPrimaryButton
                  testId="landing-nav-cta"
                  onClick={() => handlePrimaryClick("nav_cta")}
                >
                  {isAuthenticated ? t("cta.signedIn") : t("nav.startFree")}
                </NavPrimaryButton>
              </div>
              <button
                className="figma-c__nav-toggle"
                type="button"
                data-test-id="landing-nav-toggle"
                aria-label={t("nav.toggleMenu")}
                aria-expanded={mobileMenuOpen}
                onClick={() => setMobileMenuOpen((prev) => !prev)}
              >
                <motion.span
                  className="figma-c__nav-toggle-lines"
                  animate={reduced ? undefined : { rotate: mobileMenuOpen ? 45 : 0 }}
                >
                  <span />
                  <span style={{ opacity: mobileMenuOpen ? 0 : 1 }} />
                  <span
                    style={{
                      transform: mobileMenuOpen ? "translateY(-6px)" : "none",
                    }}
                  />
                </motion.span>
              </button>
            </div>
          </div>
          <div className="figma-c__mobile-menu">
            <AnimatePresence>
              {mobileMenuOpen ? (
                <motion.div
                  className="figma-c__mobile-panel"
                  initial={reduced ? { opacity: 1 } : { opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={reduced ? { opacity: 0 } : { opacity: 0, height: 0 }}
                  transition={{ duration: 0.26 }}
                >
                  <div className="figma-c__mobile-inner">
                    <a
                      className="figma-c__mobile-link"
                      href="#pricing"
                      data-test-id="landing-mobile-nav-pricing"
                      onClick={closeMenu}
                    >
                      {t("nav.pricing")}
                    </a>
                    <a
                      className="figma-c__mobile-link"
                      href="#faq"
                      data-test-id="landing-mobile-nav-faq"
                      onClick={closeMenu}
                    >
                      {t("nav.faq")}
                    </a>
                    <a
                      className="figma-c__mobile-link"
                      href="/sign-in"
                      data-test-id="landing-mobile-nav-log-in"
                      onClick={closeMenu}
                    >
                      {t("nav.logIn")}
                    </a>
                    <NavPrimaryButton
                      testId="landing-mobile-nav-cta"
                      onClick={() => handlePrimaryClick("mobile_nav_cta")}
                    >
                      {primaryCtaLabel}
                    </NavPrimaryButton>
                  </div>
                </motion.div>
              ) : null}
            </AnimatePresence>
          </div>
        </nav>
      </div>

      {/* ── 2. HERO ────────────────────────────────────────────────── */}
      <section id="top" className="figma-a__hero">
        <div className="figma-a__hero-inner">
          <div className="figma-a__hero-copy figma-a__hero-copy--top">
            <FadeUp delay={0.02}>
              <span className="figma-a__eyebrow">{t("hero.eyebrow")}</span>
            </FadeUp>
            <FadeUp delay={0.06}>
              <h1 className="figma-a__hero-title">
                {/* One line with a real space, as legacy writes it. No `<br />`:
                    JSX would drop the space beside it, and it forces a second
                    line at widths where legacy takes one. */}
                {t.rich("hero.title", { accent })}
              </h1>
            </FadeUp>
          </div>

          <FadeRight delay={0.14} className="figma-a__hero-image-wrap">
            <div className="figma-a__hero-image">
              <img src={assetSrc(heroImage)} alt={t("hero.imageAlt")} />
            </div>
          </FadeRight>

          <div className="figma-a__hero-copy figma-a__hero-copy--bottom">
            <FadeUp delay={0.12}>
              <p className="figma-a__hero-subtitle">{t("hero.subtitle")}</p>
            </FadeUp>
            <ScaleIn delay={0.18}>
              <div className="figma-a__hero-cta">
                <button
                  className="figma-a__button"
                  type="button"
                  data-test-id="landing-cta-hero"
                  onClick={() => handlePrimaryClick(offersLogIn ? HERO_LOG_IN_CTA : "hero_primary")}
                >
                  {isAuthenticated ? (
                    t("cta.signedIn")
                  ) : (
                    // Both labels, and the stylesheet shows one: the static
                    // HTML has to be right before React has run.
                    <>
                      <span className="figma-a__hero-label-new">{t("cta.startJournal")}</span>
                      <span className="figma-a__hero-label-returning">{t("nav.logIn")}</span>
                    </>
                  )}
                </button>
              </div>
              {!isAuthenticated && (
                <p className="figma-a__hero-new-here" data-test-id="landing-hero-new-here">
                  {t.rich("hero.newHere", {
                    link: (chunks) => (
                      <a
                        href="/sign-up"
                        className="figma-a__hero-new-here-link"
                        data-test-id="landing-cta-new-here"
                        onClick={handleNewHereClick}
                      >
                        {chunks}
                      </a>
                    ),
                  })}
                </p>
              )}
            </ScaleIn>
            <FadeIn delay={0.24}>
              <p className="figma-a__hero-trust">{t("hero.trust")}</p>
            </FadeIn>
            <FadeIn delay={0.28}>
              <div className="figma-a__hero-quote">
                <p>{t("hero.quote")}</p>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ── 3. PAIN POINTS ─────────────────────────────────────────── */}
      <section className="figma-a__section figma-a__section--muted">
        <div className="figma-a__container">
          <FadeUp>
            <span className="figma-a__eyebrow">{t("painPoints.eyebrow")}</span>
            <h2 className="figma-a__section-title">{t("painPoints.title")}</h2>
          </FadeUp>

          <div className="figma-a__grid figma-a__grid--three" style={{ marginTop: 48 }}>
            {painCards.map((card, i) => (
              <FadeUp key={card.id} delay={stagger(i, 0.05)} className="figma-a__card-stretch">
                <article className="figma-a__card">
                  <img
                    className="figma-a__card-icon"
                    src={assetSrc(card.icon)}
                    alt=""
                    aria-hidden="true"
                  />
                  <h3 className="figma-a__card-title">{t(`painPoints.cards.${card.id}.title`)}</h3>
                  <p className="figma-a__card-body">{t(`painPoints.cards.${card.id}.body`)}</p>
                </article>
              </FadeUp>
            ))}
          </div>

          <FadeIn delay={0.28}>
            <p className="figma-a__punchline" style={{ marginTop: 48 }}>
              {t("painPoints.punchline")}
            </p>
          </FadeIn>
        </div>
      </section>

      {afterPainPoints}

      {/* ── 4. TIME CAPSULE VISION ─────────────────────────────────── */}
      <section className="figma-a__section figma-a__section--dark">
        <div className="figma-a__narrow">
          <FadeUp>
            <span className="figma-a__eyebrow">{t("vision.eyebrow")}</span>
          </FadeUp>
          <FadeUp delay={0.06}>
            <h2 className="figma-a__section-title figma-a__section-title--large">
              {t.rich("vision.title", { accent })}
            </h2>
          </FadeUp>
          <FadeUp delay={0.12}>
            <p className="figma-a__section-body">{t("vision.body")}</p>
          </FadeUp>
        </div>
      </section>

      {afterVision}

      {/* ── 5. HOW REFLECT HELPS (Feature grid) ────────────────────── */}
      <section className="figma-a__section">
        <div className="figma-a__container">
          <FadeUp>
            <span className="figma-a__eyebrow">{t("features.eyebrow")}</span>
            <h2 className="figma-a__section-title">{t("features.title")}</h2>
          </FadeUp>

          <div className="figma-a__grid figma-a__grid--two" style={{ marginTop: 48 }}>
            {featureCards.map((id, i) => (
              <FadeUp key={id} delay={stagger(i, 0.05)} className="figma-a__card-stretch">
                <article className="figma-a__card figma-a__card--bordered">
                  <span className="figma-a__badge">{t(`features.cards.${id}.badge`)}</span>
                  <h3 className="figma-a__card-title">{t(`features.cards.${id}.title`)}</h3>
                  <p className="figma-a__card-body">{t(`features.cards.${id}.body`)}</p>
                </article>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ── 6. HOW IT WORKS (Stepper) ──────────────────────────────── */}
      <section id="how-it-works" className="figma-a__section figma-a__section--muted">
        <div className="figma-a__container">
          <div className="figma-a__narrow">
            <FadeUp>
              <span className="figma-a__eyebrow">{t("process.eyebrow")}</span>
              <h2 className="figma-a__section-title">{t("process.title")}</h2>
            </FadeUp>
            <FadeUp delay={0.06}>
              <p className="figma-a__section-body">{t("process.body")}</p>
            </FadeUp>
          </div>

          <FadeIn delay={0.12}>
            <div className="figma-a__stepper" style={{ marginTop: 48 }}>
              {steps.map((step, i) => (
                <div className="figma-a__step" key={step.num}>
                  <div className="figma-a__step-number">
                    <span className="figma-a__step-number-zero">{0}</span>
                    <span className="figma-a__step-number-digit">{step.num}</span>
                  </div>
                  <div className="figma-a__step-line">
                    <StepperDot />
                    {i < steps.length - 1 ? <div className="figma-a__step-connector" /> : null}
                  </div>
                </div>
              ))}
            </div>
          </FadeIn>

          <div className="figma-a__grid figma-a__grid--four" style={{ marginTop: 32 }}>
            {steps.map((step, i) => (
              <FadeUp key={step.id} delay={stagger(i, 0.04)} className="figma-a__card-stretch">
                <article className="figma-a__card">
                  <div className="figma-a__step-card-number" aria-hidden="true">
                    <span className="figma-a__step-number-zero">{0}</span>
                    <span className="figma-a__step-number-digit">{step.num}</span>
                  </div>
                  <h3 className="figma-a__card-title">{t(`process.steps.${step.id}.title`)}</h3>
                  <p className="figma-a__card-body">{t(`process.steps.${step.id}.body`)}</p>
                </article>
              </FadeUp>
            ))}
          </div>

          <FadeIn delay={0.3}>
            <p className="figma-a__punchline figma-a__punchline--center" style={{ marginTop: 48 }}>
              {t("process.punchline")}
            </p>
          </FadeIn>

          <ScaleIn delay={0.36} className="figma-a__cta-row">
            <button
              className="figma-a__button"
              type="button"
              data-test-id="landing-cta-stepper"
              onClick={() => handlePrimaryClick("stepper_cta")}
            >
              {t("process.cta")}
            </button>
          </ScaleIn>
        </div>
      </section>

      {/* ── 7. TIME CAPSULE PAYOFF (Split) ─────────────────────────── */}
      <section className="figma-a__section">
        <div className="figma-a__container">
          <div className="figma-a__split">
            <FadeLeft>
              <div>
                <span className="figma-a__eyebrow">{t("payoff.eyebrow")}</span>
                <h2 className="figma-a__section-title">{t("payoff.title")}</h2>

                <div className="figma-a__quote-box" style={{ marginTop: 40 }}>
                  <p>{t("payoff.quote.first")}</p>
                  <p>
                    {t.rich("payoff.quote.second", {
                      emphasis: (chunks) => (
                        <strong>
                          <em>{chunks}</em>
                        </strong>
                      ),
                    })}
                  </p>
                  <p>{t("payoff.quote.third")}</p>
                </div>

                <div className="figma-a__check-list" style={{ marginTop: 40 }}>
                  {checkItems.map((item) => (
                    <div className="figma-a__check-item" key={item}>
                      <img
                        className="figma-a__check-icon"
                        src={assetSrc(checkCircleIcon)}
                        alt=""
                        aria-hidden="true"
                      />
                      <span>{t(`payoff.checks.${item}`)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </FadeLeft>

            <FadeRight delay={0.1}>
              <div className="figma-a__media-frame">
                <video
                  className="figma-a__video-player"
                  data-test-id="landing-intro-video"
                  src={introVideo}
                  poster={assetSrc(timecapsuleImage)}
                  playsInline
                  preload="metadata"
                  controls
                />
              </div>
            </FadeRight>
          </div>
        </div>
      </section>

      {/* ── 8. PRICING ─────────────────────────────────────────────── */}
      <section id="pricing" className="figma-a__section figma-a__section--muted">
        <div className="figma-a__container">
          <div className="figma-a__narrow">
            <FadeUp>
              <span className="figma-a__eyebrow">{t("pricing.eyebrow")}</span>
              <h2 className="figma-a__section-title">{t("pricing.title")}</h2>
            </FadeUp>
            <FadeUp delay={0.06}>
              <p className="figma-a__section-body">{t("pricing.body")}</p>
            </FadeUp>
          </div>

          <div className="figma-a__pricing-grid" style={{ marginTop: 48 }}>
            {pricingPlans.map((plan, i) => (
              <ScaleIn key={plan.id} delay={stagger(i, 0.04)}>
                <article className="figma-a__plan">
                  <div className="figma-a__plan-meta">
                    <span className="figma-a__plan-eyebrow">
                      {t(`pricing.plans.${plan.priceKey}.eyebrow`)}
                    </span>
                    <div className="figma-a__plan-price">
                      <strong>{billingAmount(plan.priceKey)}</strong>
                      <span>{t(`pricing.plans.${plan.priceKey}.period`)}</span>
                    </div>
                    <p className="figma-a__plan-desc">{t(`pricing.plans.${plan.priceKey}.desc`)}</p>
                  </div>
                  <button
                    className="figma-a__button figma-a__button--full"
                    type="button"
                    data-test-id={`landing-plan-${plan.priceKey}`}
                    onClick={() => handlePrimaryClick(plan.id)}
                  >
                    {isAuthenticated ? t("cta.signedIn") : t("cta.startFree")}
                  </button>
                </article>
              </ScaleIn>
            ))}
          </div>
        </div>
      </section>

      {/* ── 9. WHY DIFFERENT (Dark cards) ──────────────────────────── */}
      <section className="figma-a__section figma-a__section--dark">
        <div className="figma-a__container">
          <FadeUp>
            <span className="figma-a__eyebrow">{t("different.eyebrow")}</span>
            <h2 className="figma-a__section-title">{t("different.title")}</h2>
          </FadeUp>

          <div className="figma-a__grid figma-a__grid--three" style={{ marginTop: 48 }}>
            {deepDiveCards.map((card, i) => (
              <FadeUp key={card.id} delay={stagger(i, 0.05)} className="figma-a__card-stretch">
                <article className="figma-a__card figma-a__card--dark">
                  <img
                    className="figma-a__card-icon"
                    src={assetSrc(card.icon)}
                    alt=""
                    aria-hidden="true"
                  />
                  <h3 className="figma-a__card-title">{t(`different.cards.${card.id}.title`)}</h3>
                  <p className="figma-a__card-body">{t(`different.cards.${card.id}.body`)}</p>
                </article>
              </FadeUp>
            ))}
          </div>

          <ScaleIn delay={0.24} className="figma-a__cta-row">
            <button
              className="figma-a__button figma-a__button--light-gradient"
              type="button"
              data-test-id="landing-cta-why-different"
              onClick={() => handlePrimaryClick("why_different_cta")}
            >
              {t("different.cta")}
            </button>
          </ScaleIn>
        </div>
      </section>

      {/* ── 10. TESTIMONIAL ────────────────────────────────────────── */}
      <section className="figma-a__section figma-a__section--muted">
        <div className="figma-a__container">
          <FadeUp>
            <p className="figma-a__eyebrow" style={{ textAlign: "center" }}>
              {t("testimonial.eyebrow")}
            </p>
          </FadeUp>
          <FadeUp delay={0.08}>
            <div className="figma-a__testimonial">
              <span className="figma-a__testimonial-quote-mark">{t("testimonial.quoteMark")}</span>
              <div className="figma-a__testimonial-text">
                {t.rich("testimonial.quote", {
                  strong: (chunks) => <strong>{chunks}</strong>,
                })}
              </div>
              <div className="figma-a__testimonial-author">
                <img
                  className="figma-a__testimonial-avatar"
                  src={assetSrc(testimonialImage)}
                  alt={t("testimonial.authorName")}
                />
                <div>
                  <div className="figma-a__testimonial-name">{t("testimonial.authorName")}</div>
                  <div className="figma-a__testimonial-role">{t("testimonial.authorRole")}</div>
                </div>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ── 11. FAQ ────────────────────────────────────────────────── */}
      <section id="faq" className="figma-a__section">
        <div className="figma-a__container">
          <div className="figma-a__narrow">
            <FadeUp>
              <span className="figma-a__eyebrow">{t("faq.eyebrow")}</span>
              <h2 className="figma-a__section-title">{t("faq.title")}</h2>
            </FadeUp>
            <FadeUp delay={0.06}>
              <p className="figma-a__section-body">{t("faq.body")}</p>
            </FadeUp>
          </div>
          <div className="figma-a__faq" style={{ marginTop: 48 }}>
            <div className="figma-a__faq-list">
              {faqs.map((id, index) => (
                <FAQItem
                  key={id}
                  id={id}
                  answer={t(`faq.items.${id}.a`)}
                  index={index}
                  question={t(`faq.items.${id}.q`)}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── 12. FINAL CTA ──────────────────────────────────────────── */}
      <section className="figma-a__section figma-a__section--dark">
        <div className="figma-a__narrow">
          <FadeUp>
            <span className="figma-a__eyebrow">{t("finalCta.eyebrow")}</span>
          </FadeUp>
          <FadeUp delay={0.06}>
            <h2 className="figma-a__section-title figma-a__section-title--large">
              {t.rich("finalCta.title", { accent })}
            </h2>
          </FadeUp>
          <FadeUp delay={0.12}>
            <p className="figma-a__section-body">{t("finalCta.body")}</p>
          </FadeUp>
          <ScaleIn delay={0.18} className="figma-a__cta-row">
            <button
              className="figma-a__button figma-a__button--light"
              type="button"
              data-test-id="landing-cta-final"
              onClick={() => handlePrimaryClick("final_cta")}
            >
              {t("finalCta.cta")}
            </button>
          </ScaleIn>
        </div>
      </section>

      {/* ── 13. FOOTER ─────────────────────────────────────────────── */}
      <WaitlistFooter />
    </div>
  );
};

export default FigmaLandingPageA;
